import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  Chip,
  Divider,
  IconButton,
  Link,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon,
} from '@mui/icons-material';
import { getMatchConfidenceLevel } from '../../utils/companyImportTemplate';

const CompanyDuplicateResolutionPanel = ({ open, company, onClose, onResolve }) => {
  const [selectedAction, setSelectedAction] = useState('');

  const handleResolve = () => {
    if (!selectedAction) return;

    onResolve({
      companyIndex: company._rowIndex,
      action: selectedAction,
      existingCompanyId: selectedAction.startsWith('map-')
        ? selectedAction.replace('map-', '')
        : null,
    });
  };

  const renderCompanyInfo = (data, title, isImported = false) => (
    <Paper sx={{ p: 2, mb: 2, backgroundColor: isImported ? '#f0f7ff' : '#f5f5f5' }}>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        {title}
      </Typography>
      <Box display="flex" flexDirection="column" gap={1}>
        {data.name && (
          <Box display="flex" alignItems="center" gap={1}>
            <BusinessIcon fontSize="small" color="action" />
            <Typography variant="body2">{data.name}</Typography>
          </Box>
        )}
        {data.tradeLicenseNumber && (
          <Box display="flex" alignItems="center" gap={1}>
            <BadgeIcon fontSize="small" color="action" />
            <Typography variant="body2">License: {data.tradeLicenseNumber}</Typography>
          </Box>
        )}
        {data.structureType && (
          <Typography variant="body2" color="textSecondary">
            Structure: {data.structureType}
            {data.parentCompanyName ? ` · Parent: ${data.parentCompanyName}` : ''}
          </Typography>
        )}
        {data.country && (
          <Typography variant="body2" color="textSecondary">
            Country: {data.country}
          </Typography>
        )}
        {data.email && (
          <Box display="flex" alignItems="center" gap={1}>
            <EmailIcon fontSize="small" color="action" />
            <Typography variant="body2">{data.email}</Typography>
          </Box>
        )}
        {data.phone && (
          <Box display="flex" alignItems="center" gap={1}>
            <PhoneIcon fontSize="small" color="action" />
            <Typography variant="body2">{data.phone}</Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );

  const renderMatchOption = (match, index) => {
    const confidence = getMatchConfidenceLevel(match.score);

    return (
      <Box key={match.id} sx={{ mb: 2 }}>
        <FormControlLabel
          value={`map-${match.id}`}
          control={<Radio />}
          label={(
            <Box>
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <Typography variant="body2" fontWeight={500}>
                  Match #{index + 1}
                </Typography>
                <Chip
                  label={confidence.label}
                  size="small"
                  color={confidence.color}
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
                <Chip
                  label={`${match.score}% match`}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              </Box>
            </Box>
          )}
          sx={{ alignItems: 'flex-start', mb: 1 }}
        />
        <Box sx={{ ml: 4 }}>
          {renderCompanyInfo(match, '', false)}
          {match.id && (
            <Link
              href={`/companies/${match.id}`}
              target="_blank"
              variant="caption"
              sx={{ display: 'inline-block', mb: 1 }}
            >
              View Company →
            </Link>
          )}
        </Box>
      </Box>
    );
  };

  if (!company) return null;

  const hasMatches = company.matches && company.matches.length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '900px',
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Resolve {company.status === 'duplicate' ? 'Duplicate' : 'Error'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {renderCompanyInfo(company, `Imported Company (Row #${company._rowIndex + 1})`, true)}

        {company.errors && company.errors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Validation Errors:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {company.errors.map((error, idx) => (
                <li key={idx}>
                  <Typography variant="body2">{error}</Typography>
                </li>
              ))}
            </ul>
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Resolution Options
        </Typography>

        <RadioGroup value={selectedAction} onChange={(e) => setSelectedAction(e.target.value)}>
          {hasMatches && (
            <>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Found {company.matches.length} potential {company.matches.length === 1 ? 'match' : 'matches'} in the system:
              </Typography>
              {company.matches.map((match, index) => renderMatchOption(match, index))}
              <Divider sx={{ my: 2 }} />
            </>
          )}

          <FormControlLabel
            value="create-new"
            control={<Radio />}
            label={(
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  Create as New Company
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Add this as a new company in the system
                </Typography>
              </Box>
            )}
            sx={{ mb: 2 }}
          />

          <FormControlLabel
            value="skip"
            control={<Radio />}
            label={(
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  Skip This Entry
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Do not import this company
                </Typography>
              </Box>
            )}
          />
        </RadioGroup>

        {hasMatches && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="caption">
              <strong>Tip:</strong> Choose a match if this company already exists. Choose &quot;Create as New&quot; only if it is a different entity.
            </Typography>
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleResolve}
          disabled={!selectedAction}
        >
          Apply Resolution
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompanyDuplicateResolutionPanel;
